import React, {
  createContext,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Hub } from "aws-amplify";
import { UserContext } from "./UserContext";
import {
  DbUser,
  IPassbackDetails,
  UniversalModalCtx,
  UserCtx,
  UserSubmission,
} from "../interfaces";
import { usePageVisibility } from "../hooks/usePageVisibility";
import { MacsAuthenticator } from "../hooks/useMacsAuthenticator";
import { _AuthType } from "../enums";
import { ErrorToast } from "../components/v2/utils/Toast";
import { Box, Stack } from "@mui/material";
import { UserQuizSubmission } from "@backend-types";
import { UniversalModalContext } from "./UniversalModalContext";
import { removeQuizDupes } from "../Helpers";

import type {
  CanvasSyncErrorItem,
  CanvasSyncStage2CountsPayload,
  CanvasSyncStage3UserPayload,
  CanvasSyncFinalPayload,
  User,
} from "@backend-types"; // if your prisma User is exported here

function mergeCanvasSyncErrors(
  prev: CanvasSyncErrorItem[] | undefined,
  next: CanvasSyncErrorItem[] | CanvasSyncErrorItem | undefined
): CanvasSyncErrorItem[] {
  const incoming = !next ? [] : Array.isArray(next) ? next : [next];
  if (!incoming.length) return prev ?? [];
  const merged = [...(prev ?? [])];
  for (const err of incoming) {
    if (!merged.some((e) => e.code === err.code && e.message === err.message)) {
      merged.push(err);
    }
  }
  return merged;
}

export interface IWebsocketContext {
  ready: boolean;
  data: null | any;
  send: (message: any) => void;
  canvasSyncData: CanvasSyncData | null;
  clearCanvasSyncData: () => void;
}

export const WebsocketContext = createContext<IWebsocketContext>({
  ready: false,
  data: null,
  send: (message: any) => {},
  canvasSyncData: null,
  clearCanvasSyncData: () => {},
  // assignmentSyncData: null,
});

interface WSErrorLog {
  error: string;
  stack?: any;
  time: Date;
}

export type CanvasSyncData = {
  provisioningStatus: "inprogress" | "success" | "failure";

  userSyncFinished?: boolean;

  // stage2 counts
  allUsersCount?: number;
  activeUsersCount?: number;

  // stage3 streamed users (committed on completion)
  users?: User[];

  // completion stats
  attemptedUsersCount?: number;
  provisionedUsersSuccessCount?: number;

  error?: unknown;
  syncErrors?: CanvasSyncErrorItem[];
};

export const WebSocketProvider: React.FC = ({ children }) => {
  const tabVisible = usePageVisibility();
  const userCtx = useContext(UserContext) as UserCtx;
  const userCtxRef = useRef(userCtx);
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const universalModalCtxRef = useRef(universalModalCtx);
  userCtxRef.current = userCtx;
  universalModalCtxRef.current = universalModalCtx;

  const [currentUser, setCurrentUser] = useState<DbUser | null>(null);

  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);

  const [canvasSyncData, setCanvasSyncData] = useState<CanvasSyncData | null>(null);
  const canvasSyncUserChunksRef = useRef<User[]>([]);

  const clearCanvasSyncData = useCallback(() => {
    canvasSyncUserChunksRef.current = [];
    setCanvasSyncData(null);
  }, []);

  const ws = useRef() as MutableRefObject<WebSocket>;
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef<number>(0);
  const errors = useRef<WSErrorLog[]>([]);

  const send = useCallback(
    (message: any) => {
      if (ready) {
        console.log("Socket ready binding message", message);
        ws.current?.send.bind(ws.current)(message);
      }
    },
    [ready]
  );

  // cache current user to minimize userCtx triggers
  useEffect(() => {
    if (!currentUser && userCtx.dbUser) {
      setCurrentUser(userCtx.dbUser);
    }
  }, [currentUser, userCtx.dbUser]);

  useEffect(() => {
    if (!ready) {
      console.log("Socket not ready");
      return;
    } else {
      send("ping");
    }
    const interval = setInterval(() => {
      send("ping");
    }, 60000);
    return () => clearInterval(interval);
  }, [ready, send, tabVisible]);

  const initializeWebSocket = useCallback(async () => {
    const setInitializationTimer = async () => {
      reconnectTimeout.current = setTimeout(() => {
        initializeWebSocket();
      }, 2000);
    };
    const clearInitializationTimer = () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
    };

    let token = "";
    token = currentUser?.connectionToken || "";
    console.log("Initializing websocket with token", token);

    if (!token) {
      const message = "No websocket connection token found";
      console.error(message);
      errors.current.push({ error: message, time: new Date() });

      if (MacsAuthenticator.authHook.authType === _AuthType.LTI) {
        ErrorToast(
          <Stack direction="row" sx={{ width: "100%" }} alignItems="center">
            <Box sx={{ flexGrow: 1, flexShrink: 1 }}>
              The grading system failed to connect. Grades may not be represented
              accurately in your class portal.
            </Box>
          </Stack>
        );
      }
      return;
    }

    if (!tabVisible) return;

    reconnectAttempts.current++;
    ws.current = new WebSocket(`${process.env.REACT_APP_WS_URL}?authorization=${token}`);

    ws.current.onerror = (e: Event) => {
      ws.current.close();
      const message = `Reattempting WS connection in 2 seconds: Attempt ${reconnectAttempts.current}`;
      console.error(message);
      setReady(false);

      errors.current.push({
        error: message,
        time: new Date(),
        stack: { type: e.type },
      });
      clearInitializationTimer();
      setInitializationTimer();
    };

    ws.current.onopen = () => {
      setReady(true);
      clearInitializationTimer();
      reconnectAttempts.current = 0;
    };

    ws.current.onmessage = (event) => {
      if (event.data === "pong") return;

      const msg = JSON.parse(event.data);
      if (!msg?.type) {
        setData(msg);
        return;
      }

      switch (msg.type) {
        case "external-submission-reset":
          window.location.reload();
          break;

        case "external-submission-result": {
          const newSub: UserSubmission | undefined = msg.submission;
          const newQuiz: UserQuizSubmission[] | undefined = msg.quizScores;

          if (newSub) {
            console.warn("Received submission via websocket", msg);

            const newUserSubmissions = userCtxRef.current.userSubmissions.filter(
              (item) => item.id !== newSub.id
            );
            newUserSubmissions.push(newSub);
            userCtxRef.current.setUserSubmissions([...newUserSubmissions]);
          }

          if (newQuiz && newQuiz.length > 0) {
            const newQuizIds = newQuiz.map((item) => item.id);
            const newUserQuizSubmissions = userCtxRef.current.userQuizSubmissions.filter(
              (item) => !newQuizIds.includes(item.id)
            );
            newUserQuizSubmissions.push(...newQuiz);
            userCtxRef.current.setUserQuizSubmissions(
              removeQuizDupes([...newUserQuizSubmissions])
            );
          }
          break;
        }

        case "grade-passback-start":
          universalModalCtxRef.current.onPassbackStart();
          console.log(msg);
          break;

        case "grade-passback-finish": {
          const details = msg?.details as IPassbackDetails | undefined;
          universalModalCtxRef.current.onPassbackFinish(msg.result ?? null, details);
          console.log(msg);
          break;
        }

        case "canvas-sync": {
          const sessionId = msg?.sessionId as string | undefined;
          const details = msg?.details as
            | CanvasSyncStage2CountsPayload
            | CanvasSyncStage3UserPayload
            | CanvasSyncFinalPayload
            | undefined;

          if (!details) {
            console.warn("canvas-sync message missing details:", msg);
            break;
          }
          if (sessionId !== MacsAuthenticator.authHook.sessionId) {
            console.warn(
              "canvas-sync message received for different session. discarding:",
              msg
            );
            break;
          }

          // If a new sync starts, you can reset buffers when you see the first stage2 message
          if (details.stage === "stage2-counts") {
            canvasSyncUserChunksRef.current = []; // new run, clear previous
            const stage2 = details as CanvasSyncStage2CountsPayload;
            setCanvasSyncData((prev) => ({
              ...(prev ?? { provisioningStatus: "inprogress" }),
              userSyncFinished: false,
              syncErrors: mergeCanvasSyncErrors(undefined, stage2.syncErrors),
              provisioningStatus: stage2.provisioningStatus,
              allUsersCount: stage2.allUsersCount,
              activeUsersCount: stage2.activeUsersCount,
              error: stage2.error,
            }));
          }

          if (details.stage === "stage3-user") {
            const stage3 = details as CanvasSyncStage3UserPayload;
            // Streamed users: collect chunks
            if (stage3.user) {
              canvasSyncUserChunksRef.current.push(stage3.user as User);
            }

            setCanvasSyncData((prev) => ({
              ...(prev ?? { provisioningStatus: "inprogress" }),
              provisioningStatus: "inprogress",
              syncErrors: mergeCanvasSyncErrors(prev?.syncErrors, stage3.syncError),
              error: prev?.error,
            }));
          }

          if (details.stage === "complete") {
            const complete = details as CanvasSyncFinalPayload;
            // Commit buffered users into canvasSyncData
            setCanvasSyncData((prev) => ({
              ...(prev ?? { provisioningStatus: "inprogress" }),
              userSyncFinished: true,
              provisioningStatus: complete.provisioningStatus,
              attemptedUsersCount: complete.attemptedUsersCount,
              provisionedUsersSuccessCount: complete.provisionedUsersSuccessCount,
              users: canvasSyncUserChunksRef.current,
              syncErrors: mergeCanvasSyncErrors(prev?.syncErrors, complete.syncErrors),
              error: complete.error ?? prev?.error,
            }));
          }

          console.log("canvas-sync", msg);
          break;
        }

        default:
          break;
      }
    };

    ws.current.onclose = (e: CloseEvent) => {
      setReady(false);
      const target = e.target as WebSocket;
      errors.current.push({
        error: "WS connection closed",
        time: new Date(),
        stack: { reason: e.reason, code: e.code, target: target.url, type: e.type },
      });
      if (!reconnectTimeout.current) {
        setInitializationTimer();
      }
    };
  }, [currentUser, tabVisible]);

  useEffect(() => {
    if (!ready && currentUser && tabVisible) {
      if (!reconnectTimeout.current) {
        reconnectTimeout.current = setTimeout(() => {
          initializeWebSocket();
        }, 2000);
      }
      return;
    }

    return () => {
      if (ws.current) {
        ws.current.close();
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
    };
  }, [ready, currentUser, initializeWebSocket, tabVisible]);

  const closeWebSocket = () => {
    ws.current.close();
  };

  Hub.listen("UI Auth", (data) => {
    if (data.payload.event === "signedout") {
      closeWebSocket();
    }
  });

  return (
    <WebsocketContext.Provider
      value={{
        ready,
        data,
        send,
        canvasSyncData,
        clearCanvasSyncData,
        // assignmentSyncData,
      }}>
      {children}
    </WebsocketContext.Provider>
  );
};
